export function debounce(fn, wait = 1) {
	let timeout;
	return function (...args) {
		clearTimeout(timeout);
		timeout = setTimeout(() => fn.call(this, ...args), wait);
	};
}

/**
 * Throttle function
 * @param {Function} cb - callback function
 * @param {number} delay - the delay in milliseconds
 * @returns {Function} the throttled callback function
 */
export const throttle = (cb, delay = 1000) => {
	let shouldWait = false;
	let waitingArgs;
	const timeoutFunc = () => {
		if (waitingArgs == null) {
			shouldWait = false;
		} else {
			cb(...waitingArgs);
			waitingArgs = null;
			setTimeout(timeoutFunc, delay);
		}
	};

	return (...args) => {
		if (shouldWait) {
			waitingArgs = args;
			return;
		}

		cb(...args);
		shouldWait = true;
		setTimeout(timeoutFunc, delay);
	};
};

// polyfills for native async delays
const requestAnimationFrame =
	window.requestAnimationFrame ||
	window.webkitRequestAnimationFrame ||
	window.mozRequestAnimationFrame ||
	(function () {
		let clock = Date.now();

		return function (callback) {
			const currentTime = +new Date();

			if (currentTime - clock > 16) {
				clock = currentTime;
				callback(currentTime);
			} else {
				// recursion wrapped in a setTimeout here
				// to unblock the browser in between loops.
				setTimeout(() => {
					// eslint-disable-next-line
					polyfill(callback);
				}, 0);
			}
		};
	})();

const requestIdleCallback =
	window.requestIdleCallback ||
	function (cb) {
		const start = Date.now();
		return setTimeout(() => {
			cb({
				didTimeout: false,
				timeRemaining() {
					return Math.max(0, 50 - (Date.now() - start));
				},
			});
		}, 1);
	};

// throttle, but only fires on trailing edge, after a delay of no similar events
// throttleDomAfterAsync :: (Function, Integer, Context) -> Function
export const throttleDomAfterAsync = (fn, ms = 0) => {
	let timer = 0;
	let previouslyRun;
	return function (...args) {
		const now = Date.now();
		clearTimeout(timer);
		if (!previouslyRun || now - previouslyRun >= ms) {
			fn(...args);
			previouslyRun = now;
		} else {
			timer = setTimeout(
				() =>
					requestIdleCallback(
						() => requestAnimationFrame(() => fn(...args)),
						{
							timeout: ms,
						}
					),
				ms
			);
		}
	};
};
